import React, { useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import SectionTitle from "../components/SectionTitle";
import GalleryCard from "../components/GalleryCard";
import CtaBanner from "../components/CtaBanner";
import { useGalleryPage, usePageBanner } from "../api/queries";
import { Images } from "lucide-react";
import { type } from "../lib/typography";

export default function GalleryPage() {
  const [filter, setFilter] = useState("All");
  const { data, isLoading: loading } = useGalleryPage();
  const { banner: galleryBanner, isLoading: bannerLoading } = usePageBanner("gallery");

  const pageHeader = data?.header ?? null;
  const items = data?.items ?? [];

  const categories = useMemo(() => {
    const typeMap = new Map();
    items.forEach((item) => {
      if (item.galleryType && !typeMap.has(item.galleryType)) {
        typeMap.set(item.galleryType, item.category || item.galleryType);
      }
    });
    if (typeMap.size === 0) return [{ value: "All", label: "All" }];
    return [
      { value: "All", label: "All" },
      ...Array.from(typeMap, ([value, label]) => ({ value, label })),
    ];
  }, [items]);

  const showCategoryFilter = categories.length > 1;

  const visible = useMemo(() => {
    if (filter === "All") return items;
    return items.filter((item) => item.galleryType === filter);
  }, [items, filter]);

  const hasActiveFilters = filter !== "All";

  return (
    <>
      <PageHeader
        title={
          pageHeader?.title ||
          galleryBanner?.title ||
          "Events, Parties & Celebrations"
        }
        breadcrumb={pageHeader?.title || galleryBanner?.title || "Gallery"}
        image={galleryBanner?.image}
        isLoading={bannerLoading}
      />

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-8 md:mb-10">
            <SectionTitle
              eyebrow={pageHeader?.tagline || "Our Memories"}
              title="Events, Parties & Celebrations"
              subtitle="Browse photos from company anniversaries, festivals, award ceremonies, and team outings."
            />
          </div>

          {!loading && items.length > 0 && showCategoryFilter && (
            <div className="mb-8 -mx-4 overflow-x-auto px-4 pb-1 scrollbar-hide sm:mx-0 sm:px-0 md:overflow-visible md:pb-0">
              <div className="flex w-max gap-2 md:w-auto md:flex-wrap md:bg-slate-100 md:p-1.5 md:rounded-lg">
                {categories.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setFilter(cat.value)}
                    className={`shrink-0 whitespace-nowrap rounded-md px-3 py-2 text-xs font-bold transition-all sm:px-4 sm:text-sm md:rounded-md ${
                      filter === cat.value
                        ? "bg-[#1e6fcf] text-white shadow-md"
                        : "border border-slate-200 bg-white text-slate-600 hover:border-[#1e6fcf]/40 hover:text-[#0a1a2f] md:border-0 md:bg-transparent"
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="rounded-xl border border-slate-200 overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-slate-200" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 bg-slate-200 rounded w-1/3" />
                    <div className="h-5 bg-slate-200 rounded w-4/5" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 rounded-xl border border-dashed border-slate-200">
              <Images className="w-12 h-12 text-[#1e6fcf]/40 mx-auto mb-4" />
              <p className={`${type.body} font-semibold`}>No photos available yet.</p>
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 rounded-xl border border-dashed border-slate-200">
              <Images className="w-12 h-12 text-[#1e6fcf]/40 mx-auto mb-4" />
              <p className={`${type.body} font-semibold`}>No photos match your filters.</p>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={() => setFilter("All")}
                  className="mt-4 text-sm font-bold text-[#1758a8] hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {visible.map((album) => (
                <GalleryCard key={album.id} album={album} />
              ))}
            </div>
          )}
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
